import {
    Box,
    Collapse,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { ChevronRight, X } from 'lucide-react';
import {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DraggableDialog, {
    DialogDragHandle,
} from '~/components/Dialogs/DraggableDialog';
import ColorSwatchPicker from '~/components/Inputs/Color/ColorSwatchPicker';
import InterpretedTypeSelect from '~/components/Inputs/InterpretedTypeSelect';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import CompactToggle from '~/components/toolbar/shared/CompactToggle';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    STAGE_ROW_TOOLTIP_PLACEMENT,
    ToggleNumberRow,
    ValueRow,
} from '~/components/toolbar/shared/StageToggleRows';
import {
    ActionButtons,
    getCustomNameError,
    Label,
} from '~/components/toolbar/OperationsToolbar/OperationFormControls';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import {
    findExternalRoiReadModel,
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { roiHasEngineGeometry } from '~/components/toolbar/OperationsToolbar/bodyContour/bodyContourParams';
import {
    BulkUnionOutcome,
    BulkUnionRequest,
    useBulkUnion,
} from '~/hooks/structure-set/useBulkUnion';
import {
    BulkUnionPreviewParams,
    bulkUnionPreviewKey,
    useBulkUnionPreview,
} from '~/hooks/structure-set/useBulkUnionPreview';
import { useRoiSdfVersions } from '~/hooks/structure-set/useRoiSdfVersions';
import {
    generateUniqueStructureNameFromList,
    STRUCTURE_NAME_MAX_LENGTH,
} from '~/domain/services/dicomProcessor';
import { cmToMm } from '~/utils/units';
import StructureChip from './StructureChip';
import { sortRois } from './roiSort';

/** Default base name of the union result, made unique before use. */
const DEFAULT_UNION_NAME = 'Union';
/**
 * Default type of the union result: untyped, ALWAYS — the default of
 * every other derived-output path (margin, ring, plain Create pass no
 * type). The structure list sorts by interpreted-type group before name
 * ({@link sortRois}), so any type this dialog stamps decides where the
 * result lands. Two regressions taught that no silent stamp survives
 * contact with that sort: an ORGAN fallback promoted a union of untyped
 * sources above every one of them, and INHERITING the sources' shared
 * type pinned a union of CTVs above the whole OAR list — the sources it
 * replaced were deleted with it, leaving the result the lone "target"
 * at the very top instead of in name order. A type is a content
 * decision; the user makes it in the Type select, and only then does
 * the grouped placement follow.
 */
const DEFAULT_INTERPRETED_TYPE = RoiInterpretedType.UNKNOWN;
/** Remove-small-parts floor offered when the stage is switched on (cm³). */
const DEFAULT_MIN_VOLUME_CM3 = 0.5;
/** Symmetric-margin bounds in cm, matching the Margin panel's magnitude. */
const MARGIN_MIN_CM = -5;
const MARGIN_MAX_CM = 5;
/**
 * Fixed stepper increment (cm) for this dialog's distance inputs — 1 mm
 * per step, snapped to one decimal. Deliberately NOT the Margin panel's
 * adaptive `marginStepCm`: a step that changes with the value read
 * as erratic wheel behaviour here, and 1 mm is the resolution union
 * margins are actually dialled in.
 */
const MARGIN_STEP_CM = 0.1;
/** Skin-margin bound in cm: a clip boundary more than 2 cm inside the
 *  EXTERNAL is no longer a skin margin. The minimum is 0 (clip at the
 *  surface); a negative value would EXPAND the clip and is refused by
 *  the pipeline. */
const SKIN_MARGIN_MAX_CM = 2;
/**
 * Delay before an automatic preview run starts, in ms. Long enough to
 * coalesce a held arrow key or a wheel spin on a numeric row into one
 * compute, short enough that a deliberate edit previews immediately.
 */
export const BULK_UNION_AUTO_PREVIEW_DELAY_MS = 250;
/** Content column width — the codebase's operation-panel width. */
const PANEL_WIDTH = 340;
/**
 * Fixed outer width of the dialog paper: the content column plus MUI
 * DialogContent's 24px side paddings. Pinned on the paper itself so the
 * dialog's width is IDENTICAL across the idle / previewing / processing
 * states — the busy captions in the actions row can therefore never widen
 * the dialog or shift the action buttons.
 */
const DIALOG_WIDTH = PANEL_WIDTH + 48;

type BulkUnionDialogProps = {
    open: boolean;
    structureSet: StructureSetReadModel;
    selectedRoiNumbers: ReadonlySet<number>;
    onClose: () => void;
    onSuccess: (outcome: BulkUnionOutcome) => void;
};

/**
 * Bulk "Union" panel (SR-WOR-H-26-2): combines the multi-selected
 * structures into one NEW structure, with an opt-in per-slice hole fill,
 * an optional margin, an optional small-component floor, a
 * keep-inside-EXTERNAL intersection (a visible toggle, default on, offered
 * only while a usable EXTERNAL exists), and an opt-in deletion of the
 * sources.
 *
 * The content is divided (the dialog's own Divider rhythm) into Sources /
 * Output (name, type, colour) / Processing / Delete-sources, with the
 * processing rows in PIPELINE ORDER — fill holes → margin → remove small
 * parts → keep inside EXTERNAL — the threshold-segmentation panel's rule
 * that the rows read as the order the stages run. The destructive
 * delete-sources opt-in sits alone in the last section, directly above
 * the actions, so it is never read as a processing stage.
 *
 * The dialog is NON-BLOCKING and draggable ({@link DraggableDialog}), not
 * modal: it renders an on-viewport preview of the union
 * ({@link useBulkUnionPreview}), and a centred modal with a backdrop would
 * cover the very viewports the preview renders in. The preview is
 * AUTOMATIC — a parameter change recomputes it rather than stranding it,
 * so the viewports always show what Apply would commit. It never writes
 * and never deletes; only Apply does, committing the displayed texture
 * instead of running the pipeline a second time.
 *
 * Every safety rule the panel enforces is mirrored from an existing one
 * rather than invented: a source counts as protected when the read model says
 * its contours cannot be modified (the same flag the rest of the UI gates on),
 * deletion goes through `removeRois` (which refuses protected members
 * all-or-nothing and disposes their undo snapshots itself), and the
 * keep-inside stage asks the ENGINE whether the EXTERNAL
 * holds geometry, exactly as the threshold-segmentation panel does.
 *
 * Every engine read (empty-source check, clip target, preview, source
 * versions) and the write go through ONE Environment: the edit target's
 * ({@link useActiveEditTarget}), which is where the user's contour edits
 * live. When no Environment holds this structure set the dialog refuses
 * with that reason instead of reading or previewing anywhere else.
 */
export default function BulkUnionDialog({
    open,
    structureSet,
    selectedRoiNumbers,
    onClose,
    onSuccess,
}: BulkUnionDialogProps) {
    const intl = useIntl();
    const editTarget = useActiveEditTarget();
    // The Environment the union is written to, and therefore the only one
    // it may be read or previewed from. Null when the edit target does not
    // resolve, or resolves for a different structure set than this dialog
    // shows.
    const environment =
        editTarget.structureSet?.dicomUid === structureSet.dicomUid
            ? (editTarget.environment ?? null)
            : null;
    const { isProcessing, runUnion } = useBulkUnion();

    const selectedRois = useMemo(
        () =>
            structureSet.roiArray
                .filter((roi) => selectedRoiNumbers.has(roi.roiNumber))
                .sort(sortRois),
        [structureSet.roiArray, selectedRoiNumbers]
    );

    // Partition once. A point structure has no volume to union and an empty
    // one contributes nothing, so both are dropped from the operation — and
    // REPORTED, because dropping a user's selection silently is exactly the
    // kind of quiet "fix" this codebase forbids.
    const { pointRois, emptyRois, usableRois } = useMemo(() => {
        const points: RoiReadModel[] = [];
        const empties: RoiReadModel[] = [];
        const usable: RoiReadModel[] = [];
        for (const roi of selectedRois) {
            if (roi.isPointRoi) {
                points.push(roi);
            } else if (
                // The ENGINE is asked, not the domain contour count: a
                // structure generated moments ago in this session carries
                // its geometry as an engine texture while the domain
                // contour array is still empty (see
                // roiHasEngineGeometry for the same reasoning).
                !roiHasEngineGeometry(
                    environment,
                    roi.roiNumber,
                    roi.contourCount
                )
            ) {
                empties.push(roi);
            } else {
                usable.push(roi);
            }
        }
        return { pointRois: points, emptyRois: empties, usableRois: usable };
    }, [selectedRois, environment]);

    // Protected sources are READ by the union (it only ever writes to a new
    // structure) and are never deleted, but the two guards part company here.
    // A locked source withdraws the sweep: a batch deletion holding a locked
    // ROI is refused in full, and the user can lift the lock. An approved one
    // is excluded by name and the rest proceeds, since unapproving is not a
    // remedy the sweep can offer. Approval outranks the lock, so a source that
    // is both takes the approved path.
    const { lockedSources, approvedSourceCount, deletableRois } =
        useMemo(() => {
            const locked = usableRois.filter(
                (roi) => roi.contourEditBlocker === 'lock'
            );
            return {
                lockedSources: locked,
                approvedSourceCount: usableRois.filter(
                    (roi) => roi.contourEditBlocker === 'approval'
                ).length,
                deletableRois:
                    locked.length > 0
                        ? []
                        : usableRois.filter(
                              (roi) => roi.contourEditBlocker === undefined
                          ),
            };
        }, [usableRois]);

    // The usable clip target: an EXTERNAL with geometry that is not itself
    // a source. Whether the clip RUNS is the user's call — see the
    // keep-inside toggle below — but only a usable target makes the toggle
    // exist at all; without one the row is absent, not disabled.
    const clipTargetExternal = useMemo(() => {
        const external = findExternalRoiReadModel(structureSet);
        if (!external || selectedRoiNumbers.has(external.roiNumber)) {
            return null;
        }
        return roiHasEngineGeometry(
            environment,
            external.roiNumber,
            external.contourCount
        )
            ? external
            : null;
    }, [structureSet, selectedRoiNumbers, environment]);

    const allRoiNames = useMemo(
        () => structureSet.roiArray.map((roi) => roi.name),
        [structureSet.roiArray]
    );
    const defaultName = useMemo(
        () =>
            generateUniqueStructureNameFromList(
                allRoiNames,
                DEFAULT_UNION_NAME
            ),
        [allRoiNames]
    );

    const defaultColor = useMemo<DicomColor>(
        () => ({ ...(usableRois[0]?.color ?? { R: 200, G: 200, B: 200 }) }),
        [usableRois]
    );

    // Seeded once per opening (the dialog is unmounted when closed), so a
    // re-render can never stomp a user's pick.
    const [name, setName] = useState('');
    const [interpretedType, setInterpretedType] = useState<RoiInterpretedType>(
        DEFAULT_INTERPRETED_TYPE
    );
    const [color, setColor] = useState<DicomColor>(defaultColor);
    // Default OFF, unlike the threshold segmentation's Body preset: a
    // union of authored structures (e.g. CTVs) may legitimately contain
    // internal gaps the user wants kept, so closing them is an explicit
    // opt-in — the same reasoning as the remove-small-parts default below.
    // The numeric fields never COMMIT an invalid value, so a stale number
    // would otherwise be applied while the box shows something else. Each
    // field reports its own validity and gates the actions.
    const [marginValid, setMarginValid] = useState(true);
    const [minVolumeValid, setMinVolumeValid] = useState(true);
    const [keepInsideMarginValid, setKeepInsideMarginValid] = useState(true);

    const [fillHolesEnabled, setFillHolesEnabled] = useState(false);
    const [marginCm, setMarginCm] = useState(0);
    const [cleanEnabled, setCleanEnabled] = useState(false);
    const [minVolumeCm3, setMinVolumeCm3] = useState(DEFAULT_MIN_VOLUME_CM3);
    // Default ON: keeping a derived structure inside the patient outline
    // is the previous (implicit) behavior and the clinically safe default;
    // the toggle exists so the raw union stays reachable. Only meaningful
    // while a usable EXTERNAL exists — the row is not rendered otherwise
    // and the state is then ignored everywhere.
    const [keepInsideEnabled, setKeepInsideEnabled] = useState(true);
    // Skin margin: how far INSIDE the EXTERNAL surface the clip boundary
    // lies (UI cm; sent as mm). Default 0 keeps today's behaviour — clip
    // exactly at the surface; a CTV that must stay a few mm inside the
    // skin dials it up. Only meaningful while the keep-inside toggle is
    // on; its input row is HIDDEN, not disabled, otherwise.
    const [keepInsideMarginCm, setKeepInsideMarginCm] = useState(0);
    const [deleteSources, setDeleteSources] = useState(false);
    // Expanded by default. The stages decide the geometry, and
    // keep-inside-EXTERNAL is ON out of the box — opening the panel with
    // an active stage folded away would hide a decision the user is
    // accountable for. Collapsing is the user's move, and the header
    // keeps reporting what is active once they make it.
    const [processingOpen, setProcessingOpen] = useState(true);

    /**
     * One-line report of the stages that will run, for the collapsed
     * header. Null when the result is the plain union — nothing to warn
     * about. Listed in the order the PIPELINE runs them, which is where
     * the run order is actually load-bearing.
     */
    const activeStageSummary = useMemo(() => {
        const parts: string[] = [];
        if (fillHolesEnabled) {
            parts.push(
                intl.formatMessage({
                    id: 'structure.bulk.union.fillHolesLabel',
                })
            );
        }
        if (marginCm !== 0) {
            parts.push(
                intl.formatMessage(
                    { id: 'structure.bulk.union.summary.margin' },
                    { value: marginCm.toFixed(1) }
                )
            );
        }
        if (cleanEnabled) {
            parts.push(
                intl.formatMessage({ id: 'structure.bulk.union.cleanLabel' })
            );
        }
        if (keepInsideEnabled && clipTargetExternal !== null) {
            parts.push(
                intl.formatMessage({
                    id: 'structure.bulk.union.summary.keepInside',
                })
            );
        }
        return parts.length > 0 ? parts.join(', ') : null;
    }, [
        intl,
        fillHolesEnabled,
        marginCm,
        cleanEnabled,
        keepInsideEnabled,
        clipTargetExternal,
    ]);

    const effectiveClipTarget =
        keepInsideEnabled && clipTargetExternal !== null
            ? clipTargetExternal
            : null;

    const nameError = useMemo(
        () => getCustomNameError(name, allRoiNames),
        [name, allRoiNames]
    );

    const existingExternal = useMemo(
        () => findExternalRoiReadModel(structureSet),
        [structureSet]
    );
    const disabledReasonId = useMemo(() => {
        if (environment === null) {
            return 'tool.disabled.gridTargetUnresolved';
        }
        if (structureSet.isApproved) {
            return 'structure.bulk.union.disabled.structureSetApproved';
        }
        if (usableRois.length === 0 && pointRois.length > 0) {
            return 'structure.bulk.union.disabled.pointsOnly';
        }
        if (usableRois.length < 2) {
            return 'structure.bulk.union.disabled.tooFewSources';
        }
        if (nameError !== null) {
            return 'structure.bulk.union.disabled.invalidName';
        }
        if (
            !marginValid ||
            (cleanEnabled && !minVolumeValid) ||
            (effectiveClipTarget !== null && !keepInsideMarginValid)
        ) {
            return 'structure.bulk.union.disabled.invalidValue';
        }
        return null;
    }, [
        environment,
        structureSet.isApproved,
        usableRois.length,
        pointRois.length,
        nameError,
        cleanEnabled,
        effectiveClipTarget,
        marginValid,
        minVolumeValid,
        keepInsideMarginValid,
    ]);

    const {
        isPreviewing,
        handlePreview,
        consumePreview,
        recolorPreview,
        abortInFlight: abortPreviewInFlight,
        cleanup: previewCleanup,
        heldKey,
    } = useBulkUnionPreview({ environment });

    // One busy flag for close prevention and button gating, so they can
    // never silently diverge (the BodyContourOperationContent shape).
    const isBusy = isProcessing || isPreviewing;

    /**
     * Every structure the pipeline reads. Their contour versions are kept
     * current by {@link useRoiSdfVersions}, so editing a source in the
     * viewports behind this non-modal panel changes the preview key and
     * re-previews — the panel can never present a union of geometry the
     * sources no longer have.
     */
    const watchedRoiNumbers = useMemo(() => {
        const numbers = usableRois.map((roi) => roi.roiNumber);
        if (clipTargetExternal !== null) {
            numbers.push(clipTargetExternal.roiNumber);
        }
        return numbers;
    }, [usableRois, clipTargetExternal]);
    const sdfVersions = useRoiSdfVersions(environment, watchedRoiNumbers);

    const previewParams = useMemo<BulkUnionPreviewParams>(
        () => ({
            sources: usableRois.map((roi) => ({
                roiNumber: roi.roiNumber,
                sdfVersion: sdfVersions.get(roi.roiNumber) ?? 0,
            })),
            fillHoles2d: fillHolesEnabled,
            marginMm: cmToMm(marginCm),
            minVolumeCm3: cleanEnabled ? minVolumeCm3 : undefined,
            keepInside: effectiveClipTarget
                ? {
                      roiNumber: effectiveClipTarget.roiNumber,
                      sdfVersion:
                          sdfVersions.get(effectiveClipTarget.roiNumber) ?? 0,
                      contractionMm: cmToMm(keepInsideMarginCm),
                  }
                : null,
        }),
        [
            usableRois,
            sdfVersions,
            fillHolesEnabled,
            marginCm,
            cleanEnabled,
            minVolumeCm3,
            effectiveClipTarget,
            keepInsideMarginCm,
        ]
    );
    const currentPreviewKey = useMemo(
        () => bulkUnionPreviewKey(previewParams),
        [previewParams]
    );

    // Latest preview inputs, read by the automatic run below without
    // making it re-run: that effect is driven by the preview KEY alone,
    // and colour is deliberately outside the key (a recolour repaints,
    // never recomputes).
    const previewInputsRef = useRef({ params: previewParams, color });
    previewInputsRef.current = { params: previewParams, color };

    /**
     * Key of the parameters a run was last STARTED for. Without it, a run
     * that ends with no held preview — an empty result, a vanished source
     * — looks to the effect below like "nothing previewed for these
     * parameters yet", and it would restart the same failing run forever.
     */
    const attemptedKeyRef = useRef<string | null>(null);

    // `previewCleanup` is bound to the Environment the held preview is drawn
    // on. When the edit target moves (another Environment, or none), this
    // releases the preview from the Environment that shows it and clears
    // the attempted key, so the automatic run below recomputes on the new
    // target instead of Apply committing a texture computed elsewhere.
    useEffect(
        () => () => {
            previewCleanup();
            attemptedKeyRef.current = null;
        },
        [previewCleanup]
    );
    // Bumped when a run is deferred because another operation owns the
    // GPU, to re-offer it without narrating the contention.
    const [previewRetry, setPreviewRetry] = useState(0);

    // The preview is AUTOMATIC: the panel shows the result of the
    // parameters as they stand, so what the viewports display is always
    // what Apply would commit. A change aborts the run in flight
    // (usePreviewBase keeps the displayed preview on screen until its
    // replacement is ready, so there is no blank frame) and schedules a
    // new one; the delay coalesces a held arrow key or a wheel spin into
    // a single compute.
    useEffect(() => {
        if (disabledReasonId !== null) {
            // Nothing previewable — an invalid name or an unusable
            // selection would only produce a refusal.
            return;
        }
        if (attemptedKeyRef.current === currentPreviewKey) {
            return;
        }
        abortPreviewInFlight();
        const timer = setTimeout(() => {
            const { params, color: previewColor } = previewInputsRef.current;
            void handlePreview(params, previewColor, { quiet: true }).then(
                (attempted) => {
                    if (attempted) {
                        attemptedKeyRef.current = currentPreviewKey;
                    } else {
                        setPreviewRetry((n) => n + 1);
                    }
                }
            );
        }, BULK_UNION_AUTO_PREVIEW_DELAY_MS);
        return () => clearTimeout(timer);
    }, [
        currentPreviewKey,
        previewRetry,
        disabledReasonId,
        abortPreviewInFlight,
        handlePreview,
    ]);

    // The on-screen preview follows the chosen color immediately — a
    // repaint of the held texture, never a recompute (color is
    // deliberately outside the preview key). Only while the held preview
    // is the CURRENT parameters' result: repainting the outgoing one
    // during a recompute would present the previous parameters' geometry
    // as this color's structure.
    useEffect(() => {
        if (heldKey === currentPreviewKey) {
            recolorPreview(color);
        }
    }, [color, heldKey, currentPreviewKey, recolorPreview]);

    // While the union or a preview runs the dialog is LOCKED: Escape and
    // the X are both inert (the threshold panel's
    // onPreventCloseChange precedent, BodyContourOperationContent). The
    // GPU pipeline is not abortable-with-rollback, so a mid-run dismissal
    // would only HIDE an operation that still completes — including the
    // opt-in IRREVERSIBLE source deletion — seconds after the user
    // believes they cancelled.
    const handleClose = useCallback(() => {
        if (!isBusy) {
            // The held preview overlay is disposed WITH the dialog: after
            // the close no control could clear it, so an orphaned outline
            // would sit in the viewports until the session ends.
            previewCleanup();
            onClose();
        }
    }, [isBusy, previewCleanup, onClose]);

    const handleApply = useCallback(async () => {
        if (disabledReasonId !== null) {
            return;
        }
        // Every pipeline field comes FROM previewParams, which is also what
        // the preview key is built from. Re-deriving them here would let the
        // committed compute drift from the key describing it.
        const request: BulkUnionRequest = {
            structureSetId: structureSet.id,
            sourceRoiNumbers: usableRois.map((roi) => roi.roiNumber),
            deleteRoiNumbers: deleteSources
                ? deletableRois.map((roi) => roi.roiNumber)
                : [],
            name: name.trim() || defaultName,
            interpretedType,
            color,
            fillHoles2d: previewParams.fillHoles2d,
            marginMm: previewParams.marginMm,
            minVolumeCm3: previewParams.minVolumeCm3,
            keepInsideRoiNumber: previewParams.keepInside?.roiNumber ?? null,
            keepInsideContractionMm: previewParams.keepInside
                ? previewParams.keepInside.contractionMm
                : 0,
        };
        // A held preview that matches the CURRENT parameters is committed
        // as the result (consumePreview key-checks and hands off the
        // texture); a stale or absent one leaves the hook to recompute.
        const outcome = await runUnion(request, () =>
            consumePreview(previewParams)
        );
        if (outcome) {
            onSuccess(outcome);
        }
    }, [
        disabledReasonId,
        structureSet.id,
        usableRois,
        deleteSources,
        deletableRois,
        name,
        defaultName,
        interpretedType,
        color,
        runUnion,
        consumePreview,
        previewParams,
        onSuccess,
    ]);

    const applyButton = (
        <ActionButtons
            onApply={handleApply}
            isApplyDisabled={disabledReasonId !== null || isBusy}
            label={intl.formatMessage({ id: 'structure.bulk.union.apply' })}
            dataTestId="union-apply-button"
        />
    );

    // Both action buttons share one disabled reason, surfaced as a
    // tooltip on whichever is hovered.
    const withDisabledReason = (button: ReactElement) =>
        disabledReasonId !== null ? (
            <DisabledHint
                title={intl.formatMessage({ id: disabledReasonId })}
                placement="bottom"
            >
                {button}
            </DisabledHint>
        ) : (
            button
        );

    return (
        // Non-blocking and draggable, NOT modal: the dialog offers an
        // on-viewport preview, so the viewports behind it must stay
        // visible and interactive (scrolling slices to inspect the result)
        // — a centred modal with a backdrop would cover exactly what the
        // preview renders. elevation 0 is the house dialog convention
        // (ConfirmationDialog & co.): without it MUI's dark-mode elevation
        // overlay lightens the paper off the app background.
        <DraggableDialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            PaperProps={{
                elevation: 0,
                sx: { width: DIALOG_WIDTH, maxWidth: DIALOG_WIDTH },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 16,
                    pb: 1,
                }}
            >
                <DialogDragHandle sx={{ flexGrow: 1, minWidth: 0 }}>
                    <FormattedMessage
                        id="structure.bulk.union.title"
                        values={{ count: usableRois.length }}
                    />
                </DialogDragHandle>
                <IconButton
                    size="small"
                    onClick={handleClose}
                    disabled={isBusy}
                    aria-label={intl.formatMessage({ id: 'close' })}
                >
                    <X size={18} />
                </IconButton>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{ width: PANEL_WIDTH, display: 'grid', gap: 1.5 }}
            >
                <Box>
                    <Label>
                        <FormattedMessage id="structure.bulk.union.sources" />
                    </Label>
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={0.5}
                        sx={{ maxHeight: 88, overflowY: 'auto' }}
                        data-testid="union-chip-list"
                    >
                        {usableRois.map((roi) => (
                            <StructureChip
                                key={roi.roiNumber}
                                roi={roi}
                                testId={`union-chip-${roi.roiNumber}`}
                                undeletableReason={roi.contourEditBlocker}
                            />
                        ))}
                    </Box>
                    {/* Set off from the chip list above (mt) so the amber
                        captions read as the sources section's own report,
                        not as helper text of the Name field that follows
                        the divider. */}
                    {pointRois.length > 0 && (
                        <Typography
                            variant="caption"
                            color="warning.main"
                            display="block"
                            sx={{ mt: 0.75 }}
                            data-testid="union-points-caption"
                        >
                            <FormattedMessage
                                id="structure.bulk.union.pointsIgnored"
                                values={{ count: pointRois.length }}
                            />
                        </Typography>
                    )}
                    {emptyRois.length > 0 && (
                        <Typography
                            variant="caption"
                            color="warning.main"
                            display="block"
                            sx={{ mt: 0.75 }}
                            data-testid="union-empty-caption"
                        >
                            <FormattedMessage
                                id="structure.bulk.union.emptyIgnored"
                                values={{ count: emptyRois.length }}
                            />
                        </Typography>
                    )}
                </Box>

                <Divider />

                <TextField
                    value={name}
                    label={intl.formatMessage({
                        id: 'structure.bulk.union.nameLabel',
                    })}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={defaultName}
                    size="small"
                    fullWidth
                    inputProps={{
                        maxLength: STRUCTURE_NAME_MAX_LENGTH,
                        style: { fontSize: 12 },
                        'data-testid': 'union-name-input',
                    }}
                    InputLabelProps={{ shrink: true }}
                    error={nameError !== null}
                    helperText={nameError}
                />

                <Box display="flex" gap={1} alignItems="flex-end">
                    <Box flexGrow={1}>
                        <Label>
                            <FormattedMessage id="structure.bulk.union.typeLabel" />
                        </Label>
                        <InterpretedTypeSelect
                            value={interpretedType}
                            onChange={setInterpretedType}
                            existingExternalName={existingExternal?.name}
                            dataTestId="union-type-select"
                        />
                    </Box>
                    <Box>
                        <Label>
                            <FormattedMessage id="structure.bulk.union.colorLabel" />
                        </Label>
                        <ColorSwatchPicker
                            color={color}
                            onChange={setColor}
                            ariaLabel={intl.formatMessage({
                                id: 'structure.bulk.union.colorLabel',
                            })}
                            dataTestId="union-color-swatch"
                            size={30}
                        />
                    </Box>
                </Box>

                <Divider />

                {/* Processing section. The rows are ordered for the
                    user — the margin first, because it is the stage a
                    union is usually dialled in on, then the opt-in
                    stages — NOT in the order the pipeline runs them
                    (fill → margin → floor → clip, which the section
                    tooltip states). Every stage is one row: label left,
                    its value right. A stage's value never sits on a line
                    of its own, so no input can read as belonging to the
                    row above it. */}
                <Box>
                    <Box
                        onClick={() => setProcessingOpen((open) => !open)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={processingOpen}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setProcessingOpen((open) => !open);
                            }
                        }}
                        data-testid="union-processing-header"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <ChevronRight
                            size={14}
                            style={{
                                flexShrink: 0,
                                transition: 'transform 150ms',
                                transform: processingOpen
                                    ? 'rotate(90deg)'
                                    : undefined,
                            }}
                        />
                        <Tooltip
                            title={
                                <FormattedMessage id="structure.bulk.union.processingTooltip" />
                            }
                            placement="bottom"
                        >
                            <Typography
                                variant="body2"
                                fontSize={11}
                                noWrap
                                color="rgba(255, 255, 255, 0.85)"
                            >
                                <FormattedMessage id="structure.bulk.union.processing.section" />
                            </Typography>
                        </Tooltip>
                        {/* Collapsed, the header still says which stages
                            will run: a geometry-changing stage the user
                            cannot see is exactly the unverifiable change
                            this panel exists to prevent. */}
                        {!processingOpen && activeStageSummary !== null && (
                            <Typography
                                variant="caption"
                                noWrap
                                color="text.secondary"
                                sx={{ minWidth: 0, flexShrink: 1 }}
                                data-testid="union-processing-summary"
                            >
                                {activeStageSummary}
                            </Typography>
                        )}
                    </Box>
                    <Collapse in={processingOpen}>
                        <Box
                            sx={{ display: 'grid', gap: 1.5, pt: 1.5 }}
                            data-testid="union-processing-rows"
                        >
                            <ValueRow
                                labelId="structure.bulk.union.marginLabel"
                                tooltipId="structure.bulk.union.marginTooltip"
                                value={marginCm}
                                onValueChange={setMarginCm}
                                onValidityChange={setMarginValid}
                                min={MARGIN_MIN_CM}
                                max={MARGIN_MAX_CM}
                                step={MARGIN_STEP_CM}
                                snapToStep
                                precision={2}
                                unit="cm"
                                dataTestId="union-margin-input"
                            />

                            <CompactToggle
                                labelId="structure.bulk.union.fillHolesLabel"
                                tooltipId="structure.bulk.union.fillHolesTooltip"
                                value={fillHolesEnabled}
                                onChange={setFillHolesEnabled}
                                dataTestId="union-fill-holes-toggle"
                                tooltipPlacement={STAGE_ROW_TOOLTIP_PLACEMENT}
                            />

                            <ToggleNumberRow
                                labelId="structure.bulk.union.cleanLabel"
                                tooltipId="structure.bulk.union.cleanTooltip"
                                enabled={cleanEnabled}
                                onEnabledChange={setCleanEnabled}
                                value={minVolumeCm3}
                                onValueChange={setMinVolumeCm3}
                                onValidityChange={setMinVolumeValid}
                                min={0}
                                max={1000}
                                step={0.1}
                                snapToStep
                                precision={2}
                                unit="cm³"
                                dataTestId="union-min-volume-input"
                                toggleDataTestId="union-clean-toggle"
                            />

                            {/* Absent — not disabled — without a usable
                                EXTERNAL: there is nothing the toggle could
                                act on. The skin margin is how far INSIDE
                                the surface the clip boundary lies. */}
                            {clipTargetExternal && (
                                <ToggleNumberRow
                                    labelId="structure.bulk.union.keepInsideLabel"
                                    tooltipId="structure.bulk.union.keepInsideTooltip"
                                    messageValues={{
                                        name: clipTargetExternal.name,
                                    }}
                                    enabled={keepInsideEnabled}
                                    onEnabledChange={setKeepInsideEnabled}
                                    value={keepInsideMarginCm}
                                    onValueChange={setKeepInsideMarginCm}
                                    onValidityChange={setKeepInsideMarginValid}
                                    min={0}
                                    max={SKIN_MARGIN_MAX_CM}
                                    step={MARGIN_STEP_CM}
                                    snapToStep
                                    precision={2}
                                    unit="cm"
                                    dataTestId="union-keep-inside-margin-input"
                                    toggleDataTestId="union-keep-inside-toggle"
                                />
                            )}
                        </Box>
                    </Collapse>
                </Box>

                <Divider />

                {/* Destructive section, last before the actions: the
                    opt-in is a CompactToggle like every other boolean row
                    in this dialog and the threshold-seg panel — one
                    control idiom for on/off options (the Checkbox idiom
                    is the multi-select lists': export items, copy
                    filters) — set off by the divider so it never reads
                    as a processing stage. */}
                <Box>
                    <CompactToggle
                        labelId="structure.bulk.union.deleteSources"
                        tooltipId="structure.bulk.union.deleteSourcesTooltip"
                        value={deleteSources && lockedSources.length === 0}
                        onChange={setDeleteSources}
                        disabled={lockedSources.length > 0}
                        dataTestId="union-delete-sources"
                        tooltipPlacement={STAGE_ROW_TOOLTIP_PLACEMENT}
                    />
                    {lockedSources.length > 0 && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            data-testid="union-delete-blocked"
                        >
                            <FormattedMessage
                                id="structure.bulk.union.deleteBlockedLocked"
                                values={{ count: lockedSources.length }}
                            />
                        </Typography>
                    )}
                    {deleteSources && lockedSources.length === 0 && (
                        <Typography
                            variant="caption"
                            color="error.main"
                            display="block"
                            data-testid="union-delete-warning"
                        >
                            <FormattedMessage
                                id="structure.bulk.union.deleteWarning"
                                values={{ count: deletableRois.length }}
                            />
                            {approvedSourceCount > 0 && (
                                <>
                                    {' '}
                                    <FormattedMessage
                                        id="structure.bulk.union.deleteApprovedKept"
                                        values={{ count: approvedSourceCount }}
                                    />
                                </>
                            )}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            {/* No Cancel button: the house shape for a non-modal draggable
                window is Preview/Apply plus the title-bar X (the margin /
                ring / threshold-seg panels' ActionButtons render Apply
                only). Cancel-in-actions is the MODAL dialogs' convention
                (CopyStructureSetDialog, ReformatDialog). Escape and the X
                remain the close path, still locked while busy. */}
            {/* px 2 / py 1.5 is the CopyStructureSetDialog actions-row
                padding — the house shape for an actions row that carries a
                status area — replacing MUI's tight default p 1. Horizontal
                inset only; the paper width stays pinned by DIALOG_WIDTH. */}
            <DialogActions sx={{ px: 2, py: 1.5 }}>
                {/* Status slot: always rendered, absorbing the row width
                    the buttons do not use (single line, ellipsized). A
                    caption appearing or vanishing therefore never moves
                    the action buttons — and the paper width is pinned by
                    DIALOG_WIDTH regardless. */}
                <Box
                    sx={{ flexGrow: 1, minWidth: 0, ml: 1 }}
                    data-testid="union-status-slot"
                >
                    {isProcessing && (
                        <Typography
                            variant="caption"
                            fontStyle="italic"
                            color="text.secondary"
                            display="block"
                            noWrap
                            data-testid="union-busy-caption"
                        >
                            <FormattedMessage id="structure.bulk.union.processing" />
                        </Typography>
                    )}
                    {isPreviewing && (
                        <Typography
                            variant="caption"
                            fontStyle="italic"
                            color="text.secondary"
                            display="block"
                            noWrap
                            data-testid="union-previewing-caption"
                        >
                            <FormattedMessage id="structure.bulk.union.previewing" />
                        </Typography>
                    )}
                </Box>
                {withDisabledReason(applyButton)}
            </DialogActions>
        </DraggableDialog>
    );
}
